import { Button, Text, VFlow } from 'bold-ui'
import { DateTime } from 'components/date/DateTime'
import { useFlags } from 'config/useFlagsContext'
import React from 'react'
import { useHistory } from 'react-router'
import { isUndefinedOrNull } from 'util/checks'
import { calculateDpp } from 'util/date/calculateDpp'
import { ACOMPANHAMENTOS_PRE_NATAL_URL } from 'view/atendimentos/detail/acompanhamentos/AcompanhamentosView'

import { RiscoGravidezTag } from '../../../pre-natal/components/RiscoGravidezTag'
import { PreNatalCardContentModel } from '../../../pre-natal/model-preNatal'
import { formatIdadeGestacional } from '../../../pre-natal/util-preNatal'
import { ContentCardSection } from '../../components/ContentCardSection'
import { UsePeriodoGestacionalResult } from '../hooks/usePeriodoGestacional'
import { IdadeGestacionalAcompanhamentoPreNatal } from '../model-acompanhamentoPreNatal'
import { PreNatalCardItems } from './PreNatalCardItems'
import { RiscoPreEclampsiaTag } from './RiscoPreEclampsiaTag'
import { TagGestacaoEncerrada } from './TagGestacaoEncerrada'

interface PreNatalCardProps {
  content: PreNatalCardContentModel
  loading: boolean
  dataUltimaConsultaOdontoGestante: LocalDate
  readOnly: boolean
  periodoGestacional?: UsePeriodoGestacionalResult
}

function renderDum(dataUltimaMenstruacao: Date, hasDumAtendimento: boolean) {
  return (
    <PreNatalCardItems<Date>
      label='DUM'
      value={dataUltimaMenstruacao}
      content={<DateTime format='DD/MM/YYYY' value={dataUltimaMenstruacao} />}
      isRegistradoAgora={hasDumAtendimento}
      dataTestId='PreNatalCard.dum'
    />
  )
}

function renderIdadeGestacional(idadeGestacional: IdadeGestacionalAcompanhamentoPreNatal, isRegistradoAgora: boolean) {
  const fraseIgSemanasDias = formatIdadeGestacional(
    idadeGestacional?.ecograficaEmDias || idadeGestacional?.cronologicaEmDias
  )
  return (
    <PreNatalCardItems<string>
      label={idadeGestacional?.ecograficaEmDias ? 'IG eco.' : 'IG cron.'}
      value={fraseIgSemanasDias}
      content={fraseIgSemanasDias}
      isRegistradoAgora={isRegistradoAgora}
      dataTestId='PreNatalCard.idadeGestacional'
    />
  )
}

function renderDppCronologica(dataUltimaMenstruacao: Date, hasDumChanged: boolean) {
  const { dpp } = calculateDpp(dataUltimaMenstruacao)

  return (
    <PreNatalCardItems<Date>
      label='DPP cronológica'
      value={dpp}
      content={<DateTime format='DD/MM/YYYY' value={dpp} />}
      isRegistradoAgora={hasDumChanged}
      dataTestId='PreNatalCard.dppCronologica'
    />
  )
}

function renderDppEcografica(dataProvavelPartoEcografica: Date, hasDppEcoChanged: boolean) {
  return (
    <PreNatalCardItems<Date>
      label='DPP ecográfica'
      value={dataProvavelPartoEcografica}
      content={<DateTime format='DD/MM/YYYY' value={dataProvavelPartoEcografica} />}
      isRegistradoAgora={hasDppEcoChanged}
      dataTestId='PreNatalCard.dppEcografica'
    />
  )
}

function renderTipoGravidez(tipoGravidez: string, hasTipoGravidezChanged: boolean) {
  return (
    <PreNatalCardItems<string>
      label='Tipo de gravidez'
      value={tipoGravidez}
      content={tipoGravidez}
      isRegistradoAgora={hasTipoGravidezChanged}
      dataTestId='PreNatalCard.tipoGravidez'
    />
  )
}

export function PreNatalCard(props: PreNatalCardProps) {
  const { dataUltimaConsultaOdontoGestante, content, loading, readOnly, periodoGestacional } = props

  const { idadeGestacional, dataProvavelPartoEcografica, hasIgEcoChanged, hasDppEcoChanged } = periodoGestacional

  const {
    dumStatus: { dumPrioritaria, hasDumChanged },
    quantidadeConsultas,
    dataUltimaConsulta,
    tipoGravidez,
    hasTipoGravidezChanged,
    isAltoRisco,
  } = content

  const hasIgChanged = hasIgEcoChanged || (hasDumChanged && !isUndefinedOrNull(idadeGestacional?.ecograficaEmDias))

  const { ACOMPANHAMENTO_PRE_NATAL_ENABLED, ALERTAS_SDC_PRE_ECLAMPSIA_ENABLED } = useFlags()

  const history = useHistory()

  const handleAcompanhamentoButtonClick = () => {
    history.push(`${ACOMPANHAMENTOS_PRE_NATAL_URL}`)
  }

  return (
    <ContentCardSection
      isLoading={loading}
      messageLoading='Carregando dados do pré-natal'
      dataTestId='PreNatalCard.container'
    >
      <VFlow vSpacing={0.5}>
        {!readOnly && <TagGestacaoEncerrada dataTestId='PreNatalCard.tagEncerramento' />}
        <RiscoGravidezTag isAltoRisco={isAltoRisco} dataTestId='PreNatalCard.tagRisco' />
        {renderDum(dumPrioritaria, hasDumChanged)}
        {renderIdadeGestacional(idadeGestacional, hasIgChanged)}
        {renderDppCronologica(dumPrioritaria, hasDumChanged)}
        {renderDppEcografica(dataProvavelPartoEcografica, hasDppEcoChanged)}
        {renderTipoGravidez(tipoGravidez, hasTipoGravidezChanged)}
        {ALERTAS_SDC_PRE_ECLAMPSIA_ENABLED && (
          <VFlow vSpacing={0.25} data-testid='PreNatalCard.riscoPreEclampsia'>
            <Text fontWeight='bold'>Risco de pré-eclâmpsia:</Text>
            <RiscoPreEclampsiaTag dataTestId='PreNatalCard.riscoPreEclampsiaTag' />
          </VFlow>
        )}
        <Text data-testid='PreNatalCard.contadorConsultas'>
          <strong>Consultas: </strong>
          {quantidadeConsultas + ' | Última em '}
          <DateTime format='DD/MM/YYYY' value={dataUltimaConsulta} />
        </Text>
        <VFlow vSpacing={0} data-testid='PreNatalCard.consultaOdontologica'>
          <Text fontWeight='bold'>Última consulta odontológica:</Text>
          {dataUltimaConsultaOdontoGestante ? (
            <DateTime format='DD/MM/YYYY' value={dataUltimaConsultaOdontoGestante} />
          ) : (
            <Text>Consulta não realizada</Text>
          )}
        </VFlow>
        {ACOMPANHAMENTO_PRE_NATAL_ENABLED && (
          <Button kind='primary' size='small' skin='outline' block onClick={handleAcompanhamentoButtonClick}>
            Ver Acompanhamento
          </Button>
        )}
      </VFlow>
    </ContentCardSection>
  )
}
